import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { Search, Copy, Clock, Banknote, Target, CheckCircle, FolderOpen } from 'lucide-react'
import { projectService } from '@/services/projectService'
import { formatCurrency } from '@/utils/formatters'
import toast from 'react-hot-toast'

const ProjectTemplates = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProject, setSelectedProject] = useState(null)
  const [showUseModal, setShowUseModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')

  // Fetch only projects marked as templates
  const { data: templateProjects = [], isLoading } = useQuery(
    ['template-projects', searchTerm],
    () => projectService.getProjects({ status: 'completed', limit: 100, search: searchTerm || undefined }),
    { select: (r) => {
      const projects = r?.data?.data?.projects || r?.data?.projects || []
      return projects.filter(p => p.is_template == true || p.is_template === 1)
    }}
  )

  const safeArray = (data) => {
    if (Array.isArray(data)) return data
    if (!data) return []
    if (typeof data === 'string') { try { return JSON.parse(data) } catch { return [] } }
    return []
  }

  const openUseModal = (project) => {
    setSelectedProject(project)
    setNewProjectName(`${project.name} (Copy)`)
    setShowUseModal(true)
  }

  const handleUseTemplate = () => {
    if (!newProjectName.trim()) { 
      toast.error('Enter project name')
      return 
    }
    setShowUseModal(false)
    // Navigate to project form with template ID
    window.location.href = `/projects/new?template=${selectedProject.id}&name=${encodeURIComponent(newProjectName)}`
  }

  const getDuration = (start, end) => {
    const days = Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24))
    return days > 30 ? `${Math.round(days / 30)} months` : `${days} days`
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Templates</h1>
          <p className="text-gray-500">Use completed projects as templates for new projects</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search templates..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg" />
        </div>
      </div>

      {/* Projects Grid */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-100 rounded-xl animate-pulse"></div>)}
        </div>
      ) : templateProjects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No templates available</p>
          <p className="text-sm text-gray-400">When completing a project, check "Make available as template" to add it here</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templateProjects.map(project => {
            const milestones = safeArray(project.milestones)
            const deliverables = milestones.reduce((acc, m) => acc + safeArray(m.deliverables).length, 0)

            return (
              <div key={project.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-xs font-medium text-gray-400">{project.code}</span>
                      <h3 className="font-semibold text-gray-900 mt-0.5">{project.name}</h3>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">Template</span>
                  </div>

                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{project.description || 'No description'}</p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>{getDuration(project.start_date, project.end_date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Banknote className="h-4 w-4 text-gray-400" />
                      <span>{formatCurrency(project.budget_allocated || 0)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Target className="h-4 w-4 text-gray-400" />
                      <span>{milestones.length} milestones</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <CheckCircle className="h-4 w-4 text-gray-400" />
                      <span>{deliverables} deliverables</span>
                    </div>
                  </div>

                  {/* Milestones Preview */}
                  {milestones.length > 0 && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 mb-2">Milestones:</p>
                      <div className="space-y-1">
                        {milestones.slice(0, 3).map((m, i) => (
                          <div key={i} className="text-sm text-gray-700 flex items-center gap-2">
                            <span className="h-1.5 w-1.5 bg-primary-500 rounded-full"></span>
                            <span className="truncate">{m.name}</span>
                          </div>
                        ))}
                        {milestones.length > 3 && <p className="text-xs text-gray-400">+{milestones.length - 3} more</p>}
                      </div>
                    </div>
                  )}

                  <button onClick={() => openUseModal(project)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm">
                    <Copy className="h-4 w-4" /> Use as Template
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Use Template Modal */}
      {showUseModal && selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Create Project from Template</h2>
              <p className="text-sm text-gray-500 mt-1">Based on: {selectedProject.name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Project Name *</label>
                <input type="text" value={newProjectName} onChange={e => setNewProjectName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg" placeholder="Enter project name" />
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">What will be copied:</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />Description & settings</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />Budget: {formatCurrency(selectedProject.budget_allocated || 0)}</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />{safeArray(selectedProject.milestones).length} milestones (reset to pending)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />{safeArray(selectedProject.risks).length} risks (reset to open)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />Duration: {getDuration(selectedProject.start_date, selectedProject.end_date)}</li>
                </ul>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowUseModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleUseTemplate}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Continue to Form
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default ProjectTemplates
